// Création de compte par lien d'invitation (issue #94).
//
// Même parti que `Login.tsx` : l'écran remplace l'application entière, et il
// passe AVANT la question de la session. Quelqu'un qui ouvre son lien dans un
// navigateur où une autre session traîne (l'exploitant qui teste, un poste
// partagé) doit voir l'invitation, pas l'application de l'autre compte.
//
// L'adresse est affichée et NON modifiable : un lien vaut pour une adresse, et
// le serveur ne regarde de toute façon que celle de l'invitation. La laisser
// éditable ferait croire qu'on peut ouvrir le compte sous un autre nom.

import { useEffect, useState } from 'react'
import { ExternalLink, LogIn, UserPlus } from 'lucide-react'
import { ApiError, post } from '../lib/api'
import { navigate } from '../lib/router'
import { useLang, useSetLang } from '../lib/lang'
import { SelecteurLangue } from '../components/selecteurLangue'
import { useT, type CleUi } from '../lib/i18n'
import type { Formule } from '../lib/formule'

/** Doit valoir `LONGUEUR_MOT_DE_PASSE` de `web/auth.ts`, comme dans
 *  `ChangerMotDePasse.tsx` : le serveur reste l'autorité. */
const LONGUEUR_MINIMALE = 12

const SITES: Record<Formule, string> = {
  radar: 'https://radar.ai-job-fit.com',
  console: 'https://console.ai-job-fit.com',
}

type Lecture =
  | { etat: 'chargement' }
  | { etat: 'valide'; email: string }
  | { etat: 'autre_formule'; formule: Formule }
  | { etat: 'expiree' | 'utilisee' | 'revoquee' | 'inconnue' | 'compte_existant' }

export function Invitation({ token, formule, onCree }: { token: string; formule: Formule; onCree: () => void }) {
  const t = useT()
  const lang = useLang()
  const setLang = useSetLang()
  const [lecture, setLecture] = useState<Lecture>({ etat: 'chargement' })
  const [motDePasse, setMotDePasse] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    post<Lecture>('/api/auth/invitation', { token })
      .then(setLecture)
      .catch((err) => {
        setLecture({ etat: 'inconnue' })
        if (err instanceof ApiError) setErreur(err.message)
      })
  }, [token])

  async function soumettre(e: React.FormEvent) {
    e.preventDefault()
    setErreur(null)
    if (motDePasse.length < LONGUEUR_MINIMALE) {
      setErreur(t('password.tooShort', { n: LONGUEUR_MINIMALE }))
      return
    }
    if (motDePasse !== confirmation) {
      setErreur(t('password.mismatch'))
      return
    }
    setEnvoi(true)
    try {
      await post('/api/auth/invitation/accepter', { token, password: motDePasse })
      onCree()
    } catch (err) {
      // 410 : le lien est mort entre l'affichage et l'envoi (expiré, servi
      // dans un autre onglet). On bascule sur l'écran qui l'explique.
      if (err instanceof ApiError && err.status === 410 && err.code) {
        setLecture({ etat: err.code as 'expiree' })
      } else if (err instanceof ApiError && err.code === 'trop_court') {
        setErreur(t('password.tooShort', { n: LONGUEUR_MINIMALE }))
      } else {
        setErreur(err instanceof ApiError ? err.message : t('invite.error.generic'))
      }
    } finally {
      setEnvoi(false)
    }
  }

  const produit = formule === 'radar' ? 'Radar' : t('login.subtitle.console')

  return (
    <div className="loginwrap">
      <SelecteurLangue className="logintoggle" value={lang} onChange={setLang} />

      {lecture.etat === 'chargement' ? (
        <div className="card logincard">
          <p className="loginsub">{t('invite.loading')}</p>
        </div>
      ) : lecture.etat === 'valide' ? (
        <form className="card logincard" onSubmit={soumettre}>
          <h1 className="loginh">{t('invite.title')}</h1>
          <p className="loginsub">{t('invite.intro', { produit })}</p>

          <label className="field-label" htmlFor="invite-email">{t('invite.email')}</label>
          {/* `autoComplete="username"` sur un champ en lecture seule : c'est
              ce qui permet au gestionnaire de mots de passe d'enregistrer le
              couple adresse et mot de passe, au lieu d'un mot de passe orphelin. */}
          <input
            id="invite-email"
            className="input-field"
            type="email"
            autoComplete="username"
            value={lecture.email}
            readOnly
          />

          <label className="field-label" htmlFor="invite-mdp">{t('login.password')}</label>
          <input
            id="invite-mdp"
            className="input-field"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            required
          />
          <p className="field-help">{t('password.rule', { n: LONGUEUR_MINIMALE })}</p>

          <label className="field-label" htmlFor="invite-confirmation">{t('password.confirm')}</label>
          <input
            id="invite-confirmation"
            className="input-field"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            required
          />

          {erreur && <p className="loginerr" role="alert">{erreur}</p>}

          <button className="btn-primary" type="submit" disabled={envoi}>
            <UserPlus size={16} />
            {envoi ? t('invite.submitting') : t('invite.submit')}
          </button>
        </form>
      ) : lecture.etat === 'autre_formule' ? (
        <div className="card logincard">
          <h1 className="loginh">{t('invite.title')}</h1>
          <p className="loginsub">{t('invite.otherProduct')}</p>
          <a className="btn-primary" href={`${SITES[lecture.formule]}/#/invitation/${token}`}>
            <ExternalLink size={16} />
            {t('invite.otherProductOpen')}
          </a>
        </div>
      ) : (
        <div className="card logincard">
          <h1 className="loginh">{t('invite.gone.title')}</h1>
          <p className="loginsub">{t(`invite.gone.${lecture.etat}` as CleUi)}</p>
          {erreur && <p className="loginerr" role="alert">{erreur}</p>}
          <button className="btn-primary" type="button" onClick={() => navigate('#/')}>
            <LogIn size={16} />
            {t('invite.toLogin')}
          </button>
        </div>
      )}
    </div>
  )
}
